import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { Course, CourseStatus } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';

const STATUS_BADGE: Record<CourseStatus, string> = {
  draft: 'bg-amber-100 text-amber-800 ring-amber-200',
  in_review: 'bg-violet-100 text-violet-800 ring-violet-200',
  published: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  archived: 'bg-slate-100 text-slate-500 ring-slate-200',
};

export default function CourseList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolledIds, setEnrolledIds] = useState<Set<number>>(() => new Set());
  const [enrollingId, setEnrollingId] = useState<number | null>(null);

  const loadCourses = async () => {
    setLoading(true);
    const response = await apiClient.getCourses();
    if (response.success && response.data) {
      const items = (response.data as any)?.results ?? response.data;
      setCourses(Array.isArray(items) ? items : []);
    } else {
      setError(response.error || 'Failed to load courses');
    }

    if (user?.role === 'student') {
      const enrollRes = await apiClient.getEnrollments();
      if (enrollRes.success && enrollRes.data) {
        const raw = enrollRes.data as { results?: unknown };
        const items = raw.results ?? enrollRes.data;
        const list = Array.isArray(items) ? items : [];
        setEnrolledIds(new Set(
          list
            .map((e: { course?: { id?: number } | number }) =>
              typeof e.course === 'object' ? e.course?.id : e.course
            )
            .filter((id): id is number => typeof id === 'number')
        ));
      }
    }

    setLoading(false);
  };

  useEffect(() => { loadCourses(); }, [user?.id, user?.role]);

  const handleDeleteCourse = async (courseId: number) => {
    if (!confirm('Delete this course? This cannot be undone.')) return;
    const response = await apiClient.deleteCourse(courseId);
    if (response.success) {
      setCourses(cs => cs.filter(c => c.id !== courseId));
    } else {
      alert('Failed to delete course');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-5">
        <p className="text-red-800 text-sm">{error}</p>
        <button onClick={loadCourses} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  const isTutor = user?.role === 'tutor';
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Courses</p>
          <h1 className="mt-0.5 text-xl font-bold text-slate-900">
            {isTutor ? 'My Courses' : isAdmin ? 'All Courses' : 'Available Courses'}
          </h1>
        </div>
        {(isTutor || isAdmin) && (
          <Link
            to="/courses/create"
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
          >
            + New Course
          </Link>
        )}
      </div>

      {/* Empty state */}
      {courses.length === 0 && (
        isTutor || isAdmin ? (
          <EmptyState
            icon="📚"
            title="You haven't created any courses yet."
            description="Create a course, add sections and lessons, then publish it for students."
            action={
              <Link
                to="/courses/create"
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Create Your First Course
              </Link>
            }
            hintsTitle="Getting started:"
            hints={[
              { icon: '🧱', label: 'Add sections and lessons' },
              { icon: '🖼️', label: 'Upload a cover image' },
              { icon: '🚀', label: 'Publish when you are ready' },
            ]}
          />
        ) : (
          <EmptyState
            icon="📚"
            title="No courses found."
            description="When courses are published, they will appear here for you to enroll."
            hintsTitle="What you can do:"
            hints={[
              { icon: '🔎', label: 'Check back for newly published courses' },
              { icon: '✉️', label: 'Ask your tutor for an invite if a course is private' },
            ]}
          />
        )
      )}

      {/* Course grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {courses.map(course => (
          <div
            key={course.id}
            className="group flex flex-col rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
          >
            {/* Cover */}
            {course.cover_image_url ? (
              <img
                src={course.cover_image_url}
                alt={course.title}
                className="h-36 w-full object-cover"
              />
            ) : (
              <div className="h-28 w-full bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center">
                <span className="text-4xl font-bold text-blue-200 select-none">
                  {course.title.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            <div className="flex flex-col flex-1 p-5">
              {/* Status / visibility badges */}
              <div className="mb-2 flex flex-wrap gap-1.5">
                {(isTutor || isAdmin) && course.status && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${STATUS_BADGE[course.status]}`}>
                    {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                  </span>
                )}
                {course.visibility && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                    course.visibility === 'private'
                      ? 'bg-indigo-50 text-indigo-800 ring-indigo-200'
                      : 'bg-sky-50 text-sky-800 ring-sky-200'
                  }`}>
                    {course.visibility === 'private' ? 'Private' : 'Public'}
                  </span>
                )}
              </div>

              <h3 className="text-base font-semibold text-slate-900 leading-snug line-clamp-2">
                {course.title}
              </h3>
              <p className="mt-1.5 text-sm text-slate-500 line-clamp-2 flex-1">
                {course.description}
              </p>

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                <span>{course.tutor?.first_name} {course.tutor?.last_name}</span>
                {course.sections_count != null && (
                  <span>{course.sections_count} section{course.sections_count !== 1 ? 's' : ''}</span>
                )}
                {course.created_at && (
                  <span>{new Date(course.created_at).toLocaleDateString()}</span>
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 flex gap-2 flex-wrap">
                <Link
                  to={`/courses/${course.id}`}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  View
                </Link>

                {(isTutor && course.tutor?.id === user?.id) || isAdmin ? (
                  <>
                    <Link
                      to={`/courses/${course.id}/edit`}
                      className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition-colors"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={async () => {
                        const res = await apiClient.cloneCourse(course.id);
                        if (res.success && res.data?.id) navigate(`/courses/${res.data.id}/edit`);
                        else alert(res.error || 'Clone failed');
                      }}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      Clone
                    </button>
                    {isAdmin && course.status === 'in_review' && (
                      <>
                        <button
                          onClick={async () => {
                            const res = await apiClient.approveCourse(course.id);
                            if (res.success) loadCourses();
                            else alert(res.error || 'Approve failed');
                          }}
                          className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const note = prompt('Rejection note (optional)') || '';
                            const res = await apiClient.rejectCourse(course.id, note);
                            if (res.success) loadCourses();
                            else alert(res.error || 'Reject failed');
                          }}
                          className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteCourse(course.id)}
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </>
                ) : null}

                {user?.role === 'student' && (
                  enrolledIds.has(course.id) ? (
                    <button
                      type="button"
                      disabled
                      className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 cursor-default"
                    >
                      Enrolled
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={enrollingId === course.id}
                      onClick={async () => {
                        if (enrollingId) return;
                        setEnrollingId(course.id);
                        const res = await apiClient.enrollInCourse(course.id);
                        setEnrollingId(null);
                        if (res.success) {
                          setEnrolledIds(prev => new Set(prev).add(course.id));
                        } else {
                          alert(res.error || 'Failed to enroll');
                        }
                      }}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60"
                    >
                      {enrollingId === course.id ? 'Enrolling…' : 'Enroll'}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
